const destinations = [
  {
    name: "France &amp; Bordeaux",
    context: "Wine trade trips, Vinexpo, Bordeaux harvest consultations, Loire Valley tourism",
    urgency: "Wine trade show and harvest calendars create non-negotiable departure windows",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Italy &amp; Spain",
    context: "Tuscany wine country, Rioja tours, olive oil sourcing, culinary travel",
    urgency: "Harvest season travel peaks in September and October with 2–4 week booking lead",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico &amp; Central America",
    context: "Mission trips from Santa Rosa churches, family visits, Guadalajara cultural travel",
    urgency: "Church mission trips have fixed departure dates tied to host organization schedules",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "British Columbia wine country, Vancouver food culture, Rocky Mountain tours",
    urgency: "Summer and fall travel peaks May through October — plan ahead",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Germany &amp; Austria",
    context: "Riesling and Gewurztraminer buying trips, Oktoberfest tourism, medical conferences",
    urgency: "Oktoberfest travel and medical conference seasons create fall urgency",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "New Zealand &amp; Australia",
    context: "Southern hemisphere wine region visits, sauvignon blanc sourcing, harvest exchange",
    urgency: "Southern hemisphere harvest (Feb–April) creates specialized travel windows",
    icon: "ri-map-pin-2-line",
  },
];

const commonSituations = [
  {
    stat: "65%",
    label: "of Santa Rosa expedited requests are wine industry, retirement, or family travel with confirmed departure dates",
  },
  {
    stat: "2–4 Weeks",
    label: "typical notice Santa Rosa wine professionals have before international business travel",
  },
  {
    stat: "55 Miles",
    label: "to the SF Passport Agency — a full-day commitment that most Santa Rosa travelers prefer to avoid",
  },
  {
    stat: "Sep–Oct",
    label: "peak Santa Rosa passport urgency season when wine harvest travel and fall tour bookings collide",
  },
];

export default function SantaRosaDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Santa Rosa Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Sonoma County clients. Understanding local travel behavior is part of why our document review catches issues before they derail your wine tour, mission trip, or retirement adventure.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm" dangerouslySetInnerHTML={{ __html: d.name }}></h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other California Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across California with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/san-francisco" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                San Francisco <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/oakland" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Oakland <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/vallejo" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Vallejo <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}